import React, { useState, useRef, useEffect } from "react";

interface MultiSelectProps {
  options: Array<{ id: number | string; name: string }>;
  selectedIds: Array<number | string>;
  onChange: (selectedIds: Array<number | string>) => void;
  placeholder?: string;
  maxWidth?: number;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedIds,
  onChange,
  placeholder = "Select...",
  maxWidth = 160,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm(""); // Clear search when dropdown closes
      return;
    }
    // Focus search input when dropdown opens
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const toggleOption = (id: number | string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((sid) => sid !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const getDisplayText = () => {
    if (selectedIds.length === 0) return placeholder;
    if (selectedIds.length === 1) {
      const option = options.find((opt) => opt.id === selectedIds[0]);
      return option?.name || placeholder;
    }
    return `${selectedIds.length} selected`;
  };

  // Filter options based on search term
  const filteredOptions = options.filter((opt) =>
    opt.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div ref={containerRef} style={{ position: "relative", maxWidth }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: "5px 30px 5px 9px",
          fontSize: "0.875rem",
          border: "1px solid var(--border-strong)",
          borderRadius: 4,
          background: "var(--surface-card)",
          cursor: "pointer",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          userSelect: "none",
          position: "relative",
        }}
      >
        <span style={{ color: selectedIds.length === 0 ? "#6c757d" : "#212529" }}>
          {getDisplayText()}
        </span>
        <i
          className="fa fa-chevron-down"
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 11,
            color: "var(--text-muted)",
          }}
        />
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: 2,
            background: "var(--surface-card)",
            border: "1px solid var(--border-strong)",
            borderRadius: 4,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            maxHeight: 250,
            overflowY: "auto",
            minWidth: "100%",
            width: "max-content",
            maxWidth: "400px",
            zIndex: 1050,
          }}
        >
          {options.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-faint)" }}>
              No options available
            </div>
          ) : (
            <>
              {/* Search Input */}
              <div
                style={{
                  padding: "8px 10px",
                  borderBottom: "1px solid var(--border-default)",
                  background: "var(--surface-card)",
                }}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: "100%",
                    padding: "4px 8px",
                    fontSize: 13,
                    border: "1px solid var(--border-strong)",
                    borderRadius: 3,
                    outline: "none",
                  }}
                  onFocus={(e) => e.target.style.borderColor = "var(--brand-primary)"}
                  onBlur={(e) => e.target.style.borderColor = "var(--border-strong)"}
                />
              </div>

              {/* Select All / Clear All */}
              <div
                style={{
                  padding: "6px 12px",
                  fontSize: 12,
                  borderBottom: "1px solid var(--border-default)",
                  background: "var(--surface-page)",
                  display: "flex",
                  gap: 10,
                  cursor: "pointer",
                }}
              >
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(filteredOptions.map((opt) => opt.id));
                  }}
                  style={{ color: "var(--brand-primary)", fontWeight: 500 }}
                >
                  Select All{searchTerm ? " (filtered)" : ""}
                </span>
                <span style={{ color: "#ddd" }}>|</span>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange([]);
                  }}
                  style={{ color: "var(--status-danger)", fontWeight: 500 }}
                >
                  Clear All
                </span>
              </div>

              {/* Options */}
              {filteredOptions.length === 0 ? (
                <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-faint)" }}>
                  No matches found
                </div>
              ) : (
                filteredOptions.map((option) => {
                const isSelected = selectedIds.includes(option.id);
                return (
                  <div
                    key={option.id}
                    onClick={() => toggleOption(option.id)}
                    style={{
                      padding: "7px 12px",
                      fontSize: 13,
                      cursor: "pointer",
                      background: isSelected ? "#e7f3ff" : "#fff",
                      borderBottom: "1px solid var(--border-subtle)",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.background = "var(--surface-page)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.background = "var(--surface-card)";
                    }}
                  >
                    <i
                      className={`fa fa-${isSelected ? "check-square" : "square-o"}`}
                      style={{
                        color: isSelected ? "#0d6efd" : "#adb5bd",
                        fontSize: 14,
                        flexShrink: 0,
                      }}
                    />
                    <span style={{
                      whiteSpace: "nowrap",
                    }}>
                      {option.name}
                    </span>
                  </div>
                );
              })
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;